import { ref } from 'vue'

export function useDragDrop(selectedItemsRef, isSelectedFn, clearSelectionFn, { onMoveItems }) {
  const isDragging = ref(false)
  const dragOverFolderId = ref(null)
  const draggedItems = ref([])

  const handleDragStart = (event, item, type) => {
    if (!isSelectedFn(item, type)) {
      clearSelectionFn()
      selectedItemsRef.value.push({ ...item, type })
    }

    draggedItems.value = [...selectedItemsRef.value]
    isDragging.value = true
    event.dataTransfer.effectAllowed = 'move'

    // Custom drag preview
    try {
      const preview = document.createElement('canvas')
      const size = 80
      const cols = 3
      const rows = 2
      preview.width = cols * (size + 6) + 12
      preview.height = rows * (size + 6) + 12
      const ctx = preview.getContext('2d')
      ctx.fillStyle = 'rgba(0,0,0,0.05)'
      ctx.strokeStyle = 'rgba(0,0,0,0.15)'
      ctx.lineWidth = 2
      ctx.fillRect(0, 0, preview.width, preview.height)
      ctx.strokeRect(1, 1, preview.width - 2, preview.height - 2)

      const items = draggedItems.value.slice(0, cols * rows)
      let i = 0
      for (const it of items) {
        const c = i % cols
        const r = Math.floor(i / cols)
        const x = 8 + c * (size + 6)
        const y = 8 + r * (size + 6)

        ctx.fillStyle = '#fff'
        ctx.strokeStyle = 'rgba(0,0,0,0.1)'
        ctx.lineWidth = 1
        ctx.fillRect(x, y, size, size)
        ctx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1)

        if (it.url || it.thumbnailUrl) {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.src = it.thumbnailUrl || it.url
          img.onload = () => {
            const ratio = Math.min(size / img.width, size / img.height)
            const w = img.width * ratio
            const h = img.height * ratio
            const dx = x + (size - w) / 2
            const dy = y + (size - h) / 2
            ctx.drawImage(img, dx, dy, w, h)
          }
        }
        i++
      }

      if (draggedItems.value.length > items.length) {
        const count = draggedItems.value.length
        const badgeR = 12
        const bx = preview.width - badgeR * 2 - 6
        const by = 6
        ctx.fillStyle = '#10b981'
        ctx.beginPath()
        ctx.arc(bx + badgeR, by + badgeR, badgeR, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = '#fff'
        ctx.font = 'bold 12px sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(count), bx + badgeR, by + badgeR)
      }

      event.dataTransfer.setDragImage(preview, preview.width / 2, preview.height / 2)
    } catch (e) {
      // fallback: use default drag image
    }
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (event, targetFolder) => {
    event.preventDefault()
    if (draggedItems.value.length > 0) {
      onMoveItems(draggedItems.value, targetFolder.path)
    }
    draggedItems.value = []
    isDragging.value = false
    dragOverFolderId.value = null
  }

  const handleDragEnter = (event, folder) => {
    event.preventDefault()
    dragOverFolderId.value = folder?.id || null
  }

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      dragOverFolderId.value = null
    }
  }

  const handleDragEnd = () => {
    isDragging.value = false
    dragOverFolderId.value = null
  }

  return {
    isDragging, dragOverFolderId,
    handleDragStart, handleDragOver, handleDrop,
    handleDragEnter, handleDragLeave, handleDragEnd
  }
}
